import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Leaf, Plus, LogOut, Eye, MessageSquare, MapPin, Users, Store, Package, CheckCircle, AlertCircle } from 'lucide-react';
import { Download } from 'lucide-react';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import StatCards from './admin/StatCards';
import FilterBar from './admin/FilterBar';
import BoxGrid from './admin/BoxGrid';
import VendorOverview from './admin/VendorOverview';
import BulkUploadBoxes from './admin/BulkUploadBoxes';

import { getErrorMessage } from '@/utils/errorHandler';
import ImageUploader from '@/components/ImageUploader';
import { categories } from '@/constants/categories';
const AdminDashboard = () => {
  const [boxes, setBoxes] = useState([]);
  const [filteredBoxes, setFilteredBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBox, setEditingBox] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [activeTab, setActiveTab] = useState('boxes'); // 'boxes', 'vendors', or 'batch'
  const [boxStatusTab, setBoxStatusTab] = useState('all'); // 'all', 'claimed', 'unclaimed'
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'name', 'vendor'
  
  // Vendor list for filter
  const [vendors, setVendors] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'homegrown',
    description: '',
    address: '',
    lat: -26.6500,
    lng: 153.0667,
    hours: '',
    contact_info: '',
    images: [],
    is_active: true,
  });
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const navigate = useNavigate();
  
  // Compute box counts by status
  const boxCounts = {
    all: boxes.length,
    claimed: boxes.filter(b => b.is_claimed !== false).length,
    unclaimed: boxes.filter(b => b.is_claimed === false).length,
  };

  useEffect(() => {
    fetchBoxes();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [boxes, searchQuery, categoryFilter, statusFilter, vendorFilter, sortBy, boxStatusTab]);

  const fetchBoxes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/boxes?include_deleted=false`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Safe data handling
      const boxData = Array.isArray(response.data) ? response.data : [];
      setBoxes(boxData);
      
      // Extract unique vendors safely
      const uniqueVendors = [...new Set(boxData.map(box => box.owner_user_id))];
      fetchVendorInfo(uniqueVendors);
    } catch (error) {
      console.error('Error fetching boxes:', error);
      toast.error('Failed to load honesty boxes');
      setBoxes([]); // Fallback
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorInfo = async (vendorIds) => {
    try {
      const token = localStorage.getItem('token');
      // Fetch all vendors from admin endpoint
      const response = await axios.get(`${API}/admin/vendors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (Array.isArray(response.data)) {
        const vendorMap = {};
        response.data.forEach(vendor => {
          vendorMap[vendor.id] = {
            email: vendor.email,
            business_name: vendor.business_name,
            display_name: vendor.business_name || vendor.email.split('@')[0]
          };
        });
        setVendors(vendorMap);
      }
    } catch (error) {
      console.error('Error fetching vendor info:', error);
      // Fallback to empty map
      setVendors({});
    }
  };

  const applyFiltersAndSort = () => {
    if (!Array.isArray(boxes)) return;
    
    let filtered = [...boxes];

    // Box status tab filter (claimed/unclaimed/all)
    if (boxStatusTab === 'claimed') {
      filtered = filtered.filter(box => box.is_claimed !== false);
    } else if (boxStatusTab === 'unclaimed') {
      filtered = filtered.filter(box => box.is_claimed === false);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(box => {
        const owner = vendors[box.owner_user_id];
        const ownerMatch = owner && (
          (owner.email && owner.email.toLowerCase().includes(query)) ||
          (owner.business_name && owner.business_name.toLowerCase().includes(query)) ||
          (owner.display_name && owner.display_name.toLowerCase().includes(query))
        );
        
        return (
          box.name.toLowerCase().includes(query) ||
          box.description.toLowerCase().includes(query) ||
          box.address.toLowerCase().includes(query) ||
          ownerMatch
        );
      });
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(box => box.category === categoryFilter);
    }

    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(box => box.is_active);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(box => !box.is_active);
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter(box => box.moderation_status === 'pending');
    }

    // Vendor filter
    if (vendorFilter !== 'all') {
      filtered = filtered.filter(box => box.owner_user_id === vendorFilter);
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'vendor':
          return (vendors[a.owner_user_id] || '').localeCompare(vendors[b.owner_user_id] || '');
        case 'recent':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    setFilteredBoxes(filtered);
  };

  const getStatistics = () => {
    return {
      total: boxes.length,
      active: boxes.filter(b => b.is_active).length,
      inactive: boxes.filter(b => !b.is_active).length,
      vendors: Object.keys(vendors).length,
    };
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const handleImagesChange = (newImages) => {
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const geocodeAddress = async (address) => {
    if (!address || address.trim().length < 5) {
      toast.error('Please enter a valid address');
      return;
    }

    setGeocoding(true);
    setGeocodeError('');

    try {
      // Use Google Maps Geocoding via backend API
      const response = await axios.post(`${API}/geocode`, {
        address: address,
        region: 'AU'
      });

      if (response.data.success) {
        const { latitude, longitude } = response.data;
        setFormData(prev => ({
          ...prev,
          lat: latitude,
          lng: longitude,
          // Keep original address, don't overwrite
        }));
        toast.success('Location found!');
      } else {
        setGeocodeError(response.data.error || 'Address not found. Please check and try again.');
        toast.error('Address not found');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setGeocodeError('Failed to find location. Please try again.');
      toast.error('Failed to find location');
    } finally {
      setGeocoding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      if (editingBox) {
        await axios.put(`${API}/vendor/boxes/${editingBox.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Honesty box updated successfully!');
      } else {
        await axios.post(`${API}/vendor/boxes`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Honesty box created successfully!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchBoxes();
    } catch (error) {
      console.error('Error saving box:', error);
      toast.error(getErrorMessage(error, 'Failed to save honesty box'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to soft delete this honesty box?')) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/admin/boxes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Honesty box deleted successfully');
      fetchBoxes();
    } catch (error) {
      console.error('Error deleting box:', error);
      toast.error('Failed to delete honesty box');
    }
  };
  const handleExportEmails = () => {
    const emailList = Object.values(vendors).filter(email => email && email !== 'Unknown Vendor');
    const uniqueEmails = [...new Set(emailList)];
    
    if (uniqueEmails.length === 0) {
      toast.error('No vendor emails found to export');
      return;
    }

    const csvContent = "data:text/csv;charset=utf-8," + "Email Address\n" + uniqueEmails.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `vendor_emails_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${uniqueEmails.length} unique vendor emails`);
  };


  const handleApprove = async (box) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/vendor/boxes/${box.id}`, 
        { 
          is_active: true, 
          moderation_status: 'approved' 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Box approved and activated!');
      fetchBoxes();
    } catch (error) {
      console.error('Error approving box:', error);
      toast.error('Failed to approve box');
    }
  };

  const handleToggleClaimed = async (box) => {
    const token = localStorage.getItem('token');
    const newClaimedStatus = box.is_claimed === false ? true : false;
    
    try {
      await axios.put(
        `${API}/admin/boxes/${box.id}/claimed-status`,
        { is_claimed: newClaimedStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (newClaimedStatus) {
        toast.success(`"${box.name}" marked as claimed`);
      } else {
        toast.success(`"${box.name}" marked as unclaimed - will show orange marker on map`);
      }
      fetchBoxes();
    } catch (error) {
      console.error('Error toggling claimed status:', error);
      toast.error('Failed to update claimed status');
    }
  };

  const handleEdit = (box) => {
    setEditingBox(box);
    setFormData({
      name: box.name,
      category: box.category,
      description: box.description,
      address: box.address,
      lat: box.lat,
      lng: box.lng,
      hours: box.hours || '',
      contact_info: box.contact_info || '',
      images: box.images || [],
      is_active: box.is_active,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingBox(null);
    setFormData({
      name: '',
      category: 'homegrown',
      description: '',
      address: '',
      lat: -26.6500,
      lng: 153.0667,
      hours: '',
      contact_info: '',
      images: [],
      is_active: true,
    });
    setGeocodeError('');
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const stats = getStatistics();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-2 border-green-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="bg-gradient-to-br from-green-400 to-green-600 p-3 rounded-2xl shadow-lg">
                <Leaf className="w-8 h-8 text-white" data-testid="dashboard-logo" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800" data-testid="dashboard-title">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Manage Honesty Boxes</p>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-2 w-full md:w-auto">
              <Button
                onClick={() => navigate('/admin/verifications')}
                variant="outline"
                className="border-blue-500 text-blue-700 hover:bg-blue-50 flex-1 md:flex-none"
                data-testid="admin-verify-btn"
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                Verify
              </Button>
              <Button
                onClick={handleExportEmails}
                variant="outline"
                className="border-orange-500 text-orange-700 hover:bg-orange-50 flex-1 md:flex-none"
                data-testid="export-emails-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Emails
              </Button>
              <Button
                onClick={() => navigate('/admin/tickets')}
                variant="outline"
                className="border-purple-500 text-purple-700 hover:bg-purple-50 flex-1 md:flex-none"
                data-testid="admin-tickets-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Tickets
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-green-500 text-green-700 hover:bg-green-50 flex-1 md:flex-none"
                data-testid="view-map-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Map
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 flex-1 md:flex-none"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-lg grid-cols-3 mb-6">
            <TabsTrigger value="boxes" className="flex items-center gap-2" data-testid="boxes-tab">
              <Store className="w-4 h-4" />
              Honesty Boxes
            </TabsTrigger>
            <TabsTrigger value="vendors" className="flex items-center gap-2" data-testid="vendors-tab">
              <Users className="w-4 h-4" />
              Vendors
            </TabsTrigger>
            <TabsTrigger value="batch" className="flex items-center gap-2" data-testid="batch-tab">
              <Package className="w-4 h-4" />
              Batch Create
            </TabsTrigger>
          </TabsList>

          <TabsContent value="boxes" className="mt-0">
            {/* Box Status Sub-Tabs */}
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={boxStatusTab === 'all' ? 'default' : 'outline'}
                  onClick={() => setBoxStatusTab('all')}
                  className={boxStatusTab === 'all' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  data-testid="box-status-all"
                >
                  <Store className="w-4 h-4 mr-2" />
                  All Boxes
                  <Badge className="ml-2 bg-blue-100 text-blue-800">{boxCounts.all}</Badge>
                </Button>
                <Button
                  variant={boxStatusTab === 'claimed' ? 'default' : 'outline'}
                  onClick={() => setBoxStatusTab('claimed')}
                  className={boxStatusTab === 'claimed' ? 'bg-green-600 hover:bg-green-700' : 'border-green-300 text-green-700'}
                  data-testid="box-status-claimed"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Claimed
                  <Badge className="ml-2 bg-green-100 text-green-800">{boxCounts.claimed}</Badge>
                </Button>
                <Button
                  variant={boxStatusTab === 'unclaimed' ? 'default' : 'outline'}
                  onClick={() => setBoxStatusTab('unclaimed')}
                  className={boxStatusTab === 'unclaimed' ? 'bg-amber-600 hover:bg-amber-700' : 'border-amber-300 text-amber-700'}
                  data-testid="box-status-unclaimed"
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Unclaimed
                  <Badge className="ml-2 bg-amber-100 text-amber-800">{boxCounts.unclaimed}</Badge>
                </Button>
              </div>
            </div>

            <StatCards stats={stats} />

            <FilterBar 
              searchQuery={searchQuery} setSearchQuery={setSearchQuery}
              categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
              statusFilter={statusFilter} setStatusFilter={setStatusFilter}
              vendorFilter={vendorFilter} setVendorFilter={setVendorFilter}
              sortBy={sortBy} setSortBy={setSortBy}
              viewMode={viewMode} setViewMode={setViewMode}
              categories={categories}
              vendors={vendors}
            />

            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800" data-testid="boxes-count">
                Showing {filteredBoxes.length} of {boxes.length} boxes
              </h2>
              <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-full px-6 py-6 shadow-lg"
                    data-testid="add-box-btn"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Honesty Box
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="box-dialog">
                  <DialogHeader>
                    <DialogTitle data-testid="dialog-title">{editingBox ? 'Edit Honesty Box' : 'Add New Honesty Box'}</DialogTitle>
                    <DialogDescription>Fill in the details for the honesty box location</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Smith Family Farm Stand"
                        required
                        data-testid="name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                        <SelectTrigger id="category" data-testid="category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.value} value={cat.value} data-testid={`cat-option-${cat.value}`}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description *</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Describe what's available..."
                        rows={3}
                        required
                        data-testid="description-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address">Address *</Label>
                      <div className="flex gap-2">
                        <Input
                          id="address"
                          value={formData.address}
                          onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                          placeholder="Enter full address (e.g., 123 Sunshine Way, Sunshine Coast QLD)"
                          required
                          data-testid="address-input"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          onClick={() => geocodeAddress(formData.address)}
                          disabled={geocoding || !formData.address}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="find-location-btn"
                        >
                          <MapPin className="w-4 h-4 mr-2" />
                          {geocoding ? 'Finding...' : 'Find'}
                        </Button>
                      </div>
                      {geocodeError && (
                        <p className="text-sm text-red-600" data-testid="geocode-error">{geocodeError}</p>
                      )}
                      {formData.lat !== -26.6500 && formData.lng !== 153.0667 && (
                        <p className="text-sm text-green-600" data-testid="location-found">
                          ✓ Location found: {formData.lat.toFixed(4)}, {formData.lng.toFixed(4)}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="lat">Latitude (auto-filled)</Label>
                        <Input
                          id="lat"
                          type="number"
                          step="any"
                          value={formData.lat}
                          onChange={(e) => setFormData(prev => ({ ...prev, lat: parseFloat(e.target.value) }))}
                          required
                          data-testid="lat-input"
                          className="bg-gray-50"
                          readOnly
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lng">Longitude (auto-filled)</Label>
                        <Input
                          id="lng"
                          type="number"
                          step="any"
                          value={formData.lng}
                          onChange={(e) => setFormData(prev => ({ ...prev, lng: parseFloat(e.target.value) }))}
                          required
                          data-testid="lng-input"
                          className="bg-gray-50"
                          readOnly
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hours">Hours (Optional)</Label>
                      <Input
                        id="hours"
                        value={formData.hours}
                        onChange={(e) => setFormData(prev => ({ ...prev, hours: e.target.value }))}
                        placeholder="e.g., 8am - 6pm daily"
                        data-testid="hours-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact">Contact Info (Optional)</Label>
                      <Input
                        id="contact"
                        value={formData.contact_info}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_info: e.target.value }))}
                        placeholder="Phone or email"
                        data-testid="contact-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Images</Label>
                      <ImageUploader 
                        images={formData.images} 
                        onImagesChange={handleImagesChange} 
                        maxImages={3}
                        onUploadStatusChange={setImageUploading}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                        data-testid="active-switch"
                      />
                      <Label htmlFor="active">Active</Label>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700" data-testid="save-box-btn" disabled={imageUploading}>
                        {imageUploading ? 'Uploading Image...' : (editingBox ? 'Update Box' : 'Create Box')}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => handleDialogClose(false)} data-testid="cancel-btn">
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <BoxGrid 
              boxes={filteredBoxes}
              loading={loading}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              handleApprove={handleApprove}
              handleToggleClaimed={handleToggleClaimed}
              categories={categories}
              vendors={vendors}
              onClearFilters={() => {
                setSearchQuery('');
                setCategoryFilter('all');
                setStatusFilter('all');
                setVendorFilter('all');
              }}
            />
          </TabsContent>

          <TabsContent value="vendors" className="mt-0">
            <VendorOverview />
          </TabsContent>

          <TabsContent value="batch" className="mt-0">
            <BulkUploadBoxes />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
